import { Box, Chip, IconButton, Typography } from "@mui/material"
import { DeleteOutlined } from "@mui-symbols-material/w300"
import type { ColumnDef } from "@tanstack/react-table"

import {
  APPROVAL_STATUS_CHIP_CLASS_MAP,
  APPROVAL_STATUS_LABELS,
  PRIORITY_STATUS_CLASS_MAP,
  PRIORITY_STATUS_LABELS,
} from "../../constants"
import type { CompanyProductAlternative } from "../../hooks/useGetAlternativesList"

import { ProductStatusCell } from "@/components/table/ProductStatusCell"
import type { Product } from "@/graphql/codegen/graphql"
import { PRODUCT_STOCK_TYPE_CHIP_COLOR, PRODUCT_STOCK_TYPE_LABEL_MAP } from "@/screens/Products/constants"
import { sortEmptyLast } from "@/utils/table"

interface UseAlternativeColumnsProps {
  onDelete: (id: string) => void
  canEdit: boolean
}

interface UseAlternativeColumnsReturn {
  columns: ColumnDef<CompanyProductAlternative>[]
}

export const useAlternativeColumns = ({
  onDelete,
  canEdit,
}: UseAlternativeColumnsProps): UseAlternativeColumnsReturn => {
  const columns: ColumnDef<CompanyProductAlternative>[] = [
    {
      id: "preferred",
      header: "Preferred",
      enableSorting: true,
      sortingFn: sortEmptyLast,
      accessorFn: (row) => {
        const status = row.alternateCompanyProductAssociationLocation?.priorityStatus
        return status ? PRIORITY_STATUS_LABELS[status] : ""
      },
      cell: ({ row }) => {
        const status = row.original.alternateCompanyProductAssociationLocation?.priorityStatus
        if (!status) return "-"
        return (
          <Chip
            size='small'
            label={PRIORITY_STATUS_LABELS[status]}
            className={`rounded-[2px] text-xs leading-[18px] ${PRIORITY_STATUS_CLASS_MAP[status]}`}
          />
        )
      },
      size: 110,
    },
    {
      id: "productId",
      header: "Product ID",
      enableSorting: true,
      sortingFn: sortEmptyLast,
      accessorFn: (row) => row.alternateCompanyProductAssociationLocation?.product?.publicId ?? "",
      cell: ({ row }) => {
        const product = row.original.alternateCompanyProductAssociationLocation?.product
        if (!product) return null
        return <ProductStatusCell product={product as unknown as Product} />
      },
      minSize: 140,
    },
    {
      id: "aeroDescription",
      header: "Akrochem Desc.",
      enableSorting: true,
      sortingFn: sortEmptyLast,
      accessorFn: (row) => row.alternateCompanyProductAssociationLocation?.product?.companyProductDescription ?? "",
      cell: ({ row }) => (
        <Typography variant='body2'>
          {row.original.alternateCompanyProductAssociationLocation?.product?.companyProductDescription ?? "-"}
        </Typography>
      ),
    },
    {
      id: "customerDescription",
      header: "Customer Desc.",
      enableSorting: true,
      sortingFn: sortEmptyLast,
      accessorFn: (row) => row.alternateCompanyProductAssociationLocation?.customerDescription ?? "",
      cell: ({ row }) => (
        <Typography variant='body2'>
          {row.original.alternateCompanyProductAssociationLocation?.customerDescription ?? "-"}
        </Typography>
      ),
    },
    {
      id: "shipFrom",
      header: "Ship From",
      enableSorting: true,
      sortingFn: sortEmptyLast,
      accessorFn: (row) =>
        row.alternateCompanyProductAssociationLocation?.product?.locationsAssociations?.[0]?.shipping?.name ?? "",
      cell: ({ row }) => {
        const name =
          row.original.alternateCompanyProductAssociationLocation?.product?.locationsAssociations?.[0]?.shipping?.name
        return <Typography variant='body2'>{name ?? "-"}</Typography>
      },
    },
    {
      id: "supplier",
      header: "Supplier",
      enableSorting: true,
      sortingFn: sortEmptyLast,
      accessorFn: (row) => row.alternateCompanyProductAssociationLocation?.product?.company?.name ?? "",
      cell: ({ row }) => {
        const name = row.original.alternateCompanyProductAssociationLocation?.product?.company?.name
        return <Typography variant='body2'>{name ?? "-"}</Typography>
      },
    },
    {
      id: "stockStatus",
      header: "Stock Status",
      enableSorting: true,
      sortingFn: sortEmptyLast,
      accessorFn: (row) => {
        const stockType = row.alternateCompanyProductAssociationLocation?.product?.stockType
        return stockType ? PRODUCT_STOCK_TYPE_LABEL_MAP[stockType] : ""
      },
      cell: ({ row }) => {
        const stockType = row.original.alternateCompanyProductAssociationLocation?.product?.stockType
        if (!stockType) return null
        return (
          <Chip
            label={PRODUCT_STOCK_TYPE_LABEL_MAP[stockType]}
            color={PRODUCT_STOCK_TYPE_CHIP_COLOR[stockType]}
            size='small'
          />
        )
      },
      size: 120,
    },
    {
      id: "custStatus",
      header: "Cust. Status",
      enableSorting: true,
      sortingFn: sortEmptyLast,
      accessorFn: (row) => {
        const status = row.alternateCompanyProductAssociationLocation?.approvalStatus
        return status ? APPROVAL_STATUS_LABELS[status] : ""
      },
      cell: ({ row }) => {
        const status = row.original.alternateCompanyProductAssociationLocation?.approvalStatus
        if (!status) return "-"
        return (
          <Chip
            variant='outlined'
            size='small'
            label={APPROVAL_STATUS_LABELS[status]}
            className={`text-xs leading-[18px] ${APPROVAL_STATUS_CHIP_CLASS_MAP[status]}`}
          />
        )
      },
      size: 120,
    },
    ...(canEdit
      ? ([
          {
            id: "actions",
            header: "",
            enableSorting: false,
            size: 48,
            cell: ({ row }) => (
              <Box className='flex justify-end'>
                <IconButton size='small' onClick={() => onDelete(row.original.alternativeId)}>
                  <DeleteOutlined />
                </IconButton>
              </Box>
            ),
          },
        ] as ColumnDef<CompanyProductAlternative>[])
      : []),
  ]

  return { columns }
}
